/**
 * How the portal shell reaches Notetaker (AIC-1437). App.tsx calls useNotetaker once and passes the result to the Shell and
 * the route switch; with the flag off it does nothing (no token read, no module, no link, no chunk).
 *
 * The entry link is temporary (plan D9): until Storage's Workspace navigation (S1) lands, it is a link in the rail's foot.
 * A follow-up moves Notetaker onto the Workspace navigation and removes this link, so the two never coexist.
 *
 * A 401 from notetaker-api hides this module; it never calls the portal-wide expire(), which stays with tracker-api's
 * own 401s, so a session that has really ended still ends through them (AIC-1486). The pages that call notetaker-api
 * (Usage, AIC-1462) keep their own load state with fetchUsage rather than going through usePortal()'s useLoad or
 * useAttempt, which expire() on any 401.
 */
import { useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import { deferredView } from "../components/DeferredView.tsx";
import type { ShellEntry, ShellModule } from "../components/Shell.tsx";
import { navigate } from "../router.tsx";
import type { Auth } from "../auth/session.ts";
import { notetakerAudience, notetakerEnabled } from "./flag.ts";
import { landingFor, notetakerNav } from "./nav.ts";
import { NOTETAKER_BASE, NOTETAKER_NAME, pageFor } from "./pages.ts";
import { holdsNotetakerRole, isNotetakerAdmin, notetakerRolesFromToken } from "./roles.ts";

// Module-scope lazy import, like the portal's other screens: the chunk loads only when a Notetaker page renders.
const Pages = deferredView(() => import("./NotetakerPages.tsx").then((m) => ({ default: m.NotetakerPages })));

export const inNotetaker = (path: string) => path === NOTETAKER_BASE || path.startsWith(`${NOTETAKER_BASE}/`);

function Redirect({ to }: { to: string }) {
  useLayoutEffect(() => navigate(to, { replace: true }), [to]);
  return null;
}

export interface NotetakerShell {
  /** The rail and bar for a Notetaker page; undefined elsewhere, so the Shell is the Software Tracker's. */
  module?: ShellModule;
  /** The temporary link into Notetaker; undefined without the flag or a Notetaker role. */
  entry?: ShellEntry;
  /** The page for a Notetaker path; null when it is not one, or Notetaker is off or not this person's (the portal's own not-found follows). */
  view: ReactNode | null;
  /** AIC-1486: false only while the token's roles are still being read, so a workspace-only person waits rather than being turned away. */
  ready: boolean;
}

/** Whether this audience shows the module to a person with these Notetaker roles (flag.ts). */
function shownTo(roles: readonly string[]): boolean {
  const audience = notetakerAudience();
  return audience === "everyone" || (audience === "role" ? holdsNotetakerRole(roles) : isNotetakerAdmin(roles));
}

/** `active`: a signed-in, real (not preview, not practice) session. The roles come from the access token, once per load. */
export function useNotetaker(auth: Pick<Auth, "getToken">, active: boolean, path: string): NotetakerShell {
  const enabled = active && notetakerEnabled();
  const [roles, setRoles] = useState<readonly string[] | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    auth.getToken().then((t) => { if (live) setRoles(notetakerRolesFromToken(t)); }, () => { if (live) setRoles([]); });
    return () => { live = false; };
  }, [auth, enabled]);
  if (!enabled) return { view: null, ready: true };
  const ready = roles !== null;
  // The roles still decide Admin, whoever the audience lets in.
  const allowed = roles !== null && shownTo(roles);
  const entry = allowed ? { to: landingFor(roles), label: NOTETAKER_NAME } : undefined;
  if (!inNotetaker(path) || (roles !== null && !allowed)) return { entry, view: null, ready };
  // Until the roles are read the rail shows Work and Analysis (Admin waits for the role), never the Software Tracker's.
  const module: ShellModule = {
    name: NOTETAKER_NAME, nav: notetakerNav(roles ?? []), title: pageFor(path)?.label ?? NOTETAKER_NAME,
    role: roles && isNotetakerAdmin(roles) ? "Notetaker admin" : "Notetaker",
  };
  if (roles === null) return { module, entry, view: <p role="status">Loading…</p>, ready };
  return { module, entry, ready, view: path.replace(/\/+$/, "") === NOTETAKER_BASE ? <Redirect to={landingFor(roles)} /> : <Pages.View path={path} roles={roles} auth={auth} /> };
}
